<template>
  <div class="guide-cta">
    <!-- Trigger button -->
    <v-btn
      class="guide-cta__trigger"
      variant="flat"
      aria-label="What's new"
      @click="openGuide"
    >
      <v-icon color="mpBlue" class="mr-2">mdi-lightbulb-on-outline</v-icon>
      <span class="font-weight-large">What's new?</span>
    </v-btn>

    <!-- Guide modal -->
    <v-dialog
      v-model="isOpen"
      persistent
      transition="dialog-bottom-transition"
      :width="DIALOG_WIDTH"
      content-class="guide-cta__dialog-content"
      class="guide-cta__dialog"
    >
      <div class="guide-cta__modal">
        <div class="guide-cta__body">
          <v-btn
            icon
            size="x-small"
            color="white"
            class="guide-cta__close"
            aria-label="Close guide"
            @click="closeGuide"
          >
            <v-icon size="small">mdi-close</v-icon>
          </v-btn>

          <!-- Loading overlay (hidden when iframe has loaded) -->
          <div v-show="isLoading" class="guide-cta__loading">
            <v-progress-linear
              indeterminate
              color="mpBlue"
              class="guide-cta__progress"
            />
          </div>

          <iframe
            v-if="isOpen"
            :src="guideUrl"
            class="guide-cta__iframe"
            title="Workflow guide"
            @load="onIframeLoad"
          />
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

const DIALOG_WIDTH = 960;

/* ---------------------------------------------------------------------------
 * Props
 * --------------------------------------------------------------------------- */
const props = withDefaults(
  defineProps<{
    guideUrl?: string;
  }>(),
  {
    guideUrl: "https://app.guideflow.com/embed/qkqdxyxu1p",
  }
);

const emit = defineEmits<{
  click: [];
}>();

/* ---------------------------------------------------------------------------
 * State
 * --------------------------------------------------------------------------- */
const isOpen = ref(false);
const isLoading = ref(true);

/* ---------------------------------------------------------------------------
 * Actions
 * --------------------------------------------------------------------------- */
function openGuide() {
  isLoading.value = true;
  isOpen.value = true;
  emit("click");
}

function closeGuide() {
  isOpen.value = false;
  isLoading.value = true; // reset for next open
}

function onIframeLoad() {
  isLoading.value = false;
}
</script>

<style lang="scss" scoped>
.guide-cta__dialog :deep(.v-overlay__content),
.guide-cta__dialog :deep(.v-dialog) {
  z-index: 9999 !important;
}

.guide-cta__dialog :deep(.guide-cta__dialog-content) {
  width: 960px;
  height: 490px;
  max-width: 95vw;
  max-height: 85vh;
  margin: auto;
}

.guide-cta__modal {
  display: flex;
  flex-direction: column;
  width: 960px;
  height: 490px;
  max-width: 95vw;
  max-height: 85vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.guide-cta__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
}

.guide-cta__close {
  position: absolute;
  top: 4px;
  right: 8px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.guide-cta__close:hover {
  background: rgba(0, 0, 0, 0.8) !important;
}

.guide-cta__close :deep(.v-icon) {
  color: #fff !important;
}

.guide-cta__loading {
  position: absolute;
  inset: 0;
  background: #fff;
  z-index: 1;
}

.guide-cta__progress {
  height: 3px !important;
}

.guide-cta__iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}
.font-weight-large {
  text-transform: none;
  font-weight: 500;
  font-size: 16px;
}
</style>
